/**
 * The scraper that clears Lucky Scratch's foil: the edge of a coin dragged across a ticket.
 * Its footprint is wide across the drag and short along it, with a torn, grainy rim and
 * streaks where the first pass leaves foil behind, so a stroke reads as scratched foil and
 * a second pass over it rubs it clean, not as a row of round holes.
 */

export type Rng = () => number;

/** The footprint, drawn once: white where it clears, transparent where foil stays. */
export interface ScratchBrush {
  canvas: HTMLCanvasElement;
  /** The footprint's size in its own pixels; it is stamped scaled to the stroke width. */
  size: number;
}

/**
 * Draws the footprint into `canvas` (`size` square). Exported apart from the DOM canvas
 * creation so the shape can be checked on any 2D context.
 */
export function paintBrush(ctx: CanvasRenderingContext2D, size: number, rand: Rng): void {
  const c = size / 2;
  // Across the drag: almost the whole width; along it: a short, slightly bowed scrape.
  const halfW = size * 0.48;
  const halfL = size * 0.2;
  ctx.clearRect(0, 0, size, size);
  // A solid core where the coin's edge presses hardest, nearly the whole footprint.
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.beginPath();
  ctx.ellipse(c, c, halfW * 0.9, halfL * 0.8, 0, 0, Math.PI * 2);
  ctx.fill();
  // A thin torn rim: small sharp chips just outside the core, so the cleared edge is ragged
  // like ripped foil rather than round or fuzzy.
  const chips = Math.round(size * 1.4);
  for (let i = 0; i < chips; i++) {
    const a = rand() * Math.PI * 2;
    const r = 0.86 + rand() * 0.16;
    const x = c + Math.cos(a) * halfW * r;
    const y = c + Math.sin(a) * halfL * r;
    ctx.fillStyle = `rgba(255,255,255,${(0.7 + 0.3 * rand()).toFixed(3)})`;
    const d = size * (0.01 + rand() * 0.022);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rand() * Math.PI);
    ctx.fillRect(-d / 2, -d / 4, d, d / 2);
    ctx.restore();
  }
  // Streaks the first pass misses: thin lines along the drag (the footprint's y) where foil
  // stays put, spread across its width.
  ctx.globalCompositeOperation = 'destination-out';
  const streaks = 5 + Math.floor(rand() * 4);
  for (let i = 0; i < streaks; i++) {
    const x = c + (rand() - 0.5) * halfW * 1.7;
    ctx.fillStyle = `rgba(0,0,0,${(0.45 + 0.4 * rand()).toFixed(3)})`;
    ctx.fillRect(x, c - halfL * 1.2, size * (0.006 + rand() * 0.012), halfL * 2.4);
  }
  ctx.globalCompositeOperation = 'source-over';
}

export function makeScratchBrush(size: number, rand: Rng = Math.random): ScratchBrush | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  paintBrush(ctx, size, rand);
  return { canvas, size };
}

/** Where to stamp along a drag from (x0, y0) to (x1, y1), and which way the footprint faces. */
export function stampPlan(x0: number, y0: number, x1: number, y1: number, width: number): { points: [number, number][]; angle: number } {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  // Footprints overlap by about three quarters, so a single pass clears its middle.
  const spacing = Math.max(1, width * 0.12);
  const steps = Math.max(1, Math.ceil(dist / spacing));
  const points: [number, number][] = [];
  for (let i = 0; i <= steps; i++) points.push([x0 + (dx * i) / steps, y0 + (dy * i) / steps]);
  // The footprint's long side (its x) lies across the drag, its y along it.
  const angle = dist > 0 ? Math.atan2(dy, dx) - Math.PI / 2 : 0;
  return { points, angle };
}
